import { Search } from "@/components/search/Search.tsx";
import { EMPLOYEE_GROUP_COLUMN_ID } from "@/pages/reporting/employee/people-analytics/people-analytics.types.ts";
import { ApiPeopleAnalyticsCatalog } from "@/types/reporting/people-analytics.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  FontAwesomeIcon,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface GroupBySelectorProps {
  catalog: ApiPeopleAnalyticsCatalog | undefined;
  groupByColumnId: string | null;
  onChange: (columnId: string | null) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GroupBySelector({
  catalog,
  groupByColumnId,
  onChange,
  open,
  onOpenChange,
}: GroupBySelectorProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const handleOpenChange = (next: boolean) => {
    if (!next) setSearch("");
    onOpenChange(next);
  };

  const groups = useMemo(
    () =>
      (catalog?.groups ?? [])
        .map((group) => ({
          id: group.group,
          title: group.title,
          columns: group.columns.filter(
            (column) => column.is_groupable && column.id !== EMPLOYEE_GROUP_COLUMN_ID
          ),
        }))
        .filter((group) => group.columns.length > 0),
    [catalog]
  );
  const allColumns = useMemo(() => groups.flatMap((group) => group.columns), [groups]);
  const hasEmployeeGrouping = useMemo(
    () =>
      (catalog?.groups ?? []).some((group) =>
        group.columns.some(
          (column) => column.id === EMPLOYEE_GROUP_COLUMN_ID && column.is_groupable
        )
      ),
    [catalog]
  );
  const employeeLabel = t("reporting:people_analytics_group_by_employee");
  const selected =
    groupByColumnId === EMPLOYEE_GROUP_COLUMN_ID
      ? { title: employeeLabel }
      : allColumns.find((column) => column.id === groupByColumnId);

  const filtered = search
    ? allColumns.filter((column) => column.title.toLowerCase().includes(search.toLowerCase()))
    : [];

  const noneLabel = t("reporting:people_analytics_group_by_none");
  const showNone = noneLabel.toLowerCase().includes(search.toLowerCase());
  const showEmployee =
    hasEmployeeGrouping && employeeLabel.toLowerCase().includes(search.toLowerCase());

  const handleSelect = (columnId: string | null) => {
    onChange(columnId);
    setSearch("");
    onOpenChange(false);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={handleOpenChange}
      closeOnClick={false}
      trigger={
        <Button
          variant="primary"
          size="sm"
          className="max-w-full"
          rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
        >
          {selected ? selected.title : t("reporting:people_analytics_group_by_placeholder")}
        </Button>
      }
    >
      <Box className="w-full p-2 pt-0">
        <Search
          value={search}
          placeholder={t("reporting:people_analytics_filter_search")}
          clearable
          onClear={() => setSearch("")}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </Box>
      {showNone && (
        <DropdownMenuItem
          title={noneLabel}
          isSelected={!groupByColumnId}
          onClick={() => handleSelect(null)}
        />
      )}
      {showEmployee && (
        <DropdownMenuItem
          title={employeeLabel}
          isSelected={groupByColumnId === EMPLOYEE_GROUP_COLUMN_ID}
          onClick={() => handleSelect(EMPLOYEE_GROUP_COLUMN_ID)}
        />
      )}
      {search
        ? filtered.map((column) => (
            <DropdownMenuItem
              key={column.id}
              title={column.title}
              isSelected={column.id === groupByColumnId}
              onClick={() => handleSelect(column.id)}
            />
          ))
        : groups.map((group) => (
            <DropdownMenuSub key={group.id} title={group.title}>
              {group.columns.map((column) => (
                <DropdownMenuItem
                  key={column.id}
                  title={column.title}
                  isSelected={column.id === groupByColumnId}
                  onClick={() => handleSelect(column.id)}
                />
              ))}
            </DropdownMenuSub>
          ))}
    </DropdownMenu>
  );
}
